/**
 * 결제 관리 페이지 (탭 인터페이스)
 * Payment Management Page with Tabbed Interface
 *
 * 2개의 결제 관리 기능을 하나의 페이지에서 탭으로 관리:
 * - 대시보드: PaymentDashboard
 * - 만료 예정 회원: ExpiringMembersList
 */

import React, { useState, Suspense } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  PlusCircleIcon,
  ListBulletIcon,
  CogIcon,
  ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { ownerLayoutService } from '../services/OwnerLayoutService';

// 탭 컴포넌트 lazy import (에러 방지)
// (결제 대시보드 탭 제거 — 매출 노출 방지. 매출은 /owner/analytics 에서 비밀번호로 보호)
const ExpiringMembersList = React.lazy(() => import('../components/ExpiringMembersList'));
const AddVoucherForm = React.lazy(() => import('../components/payment/AddVoucherForm'));
const VouchersList = React.lazy(() => import('../components/payment/VouchersList'));
const PricingManagement = React.lazy(() => import('../components/PricingManagement'));
const DiscountReasonManagement = React.lazy(() => import('../components/payment/DiscountReasonManagement'));

type TabId = 'vouchers-list' | 'add-voucher' | 'pricing-settings' | 'discount-reasons' | 'expiring-members' | 'dashboard';

interface Tab {
  id: TabId;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const tabs: Tab[] = [
  {
    id: 'vouchers-list',
    name: '이용권 관리',
    icon: ListBulletIcon,
  },
  {
    id: 'add-voucher',
    name: '이용권 발급',
    icon: PlusCircleIcon,
  },
  {
    id: 'pricing-settings',
    name: '이용권 설정',
    icon: CogIcon,
  },
  {
    id: 'discount-reasons',
    name: '할인 사유',
    icon: ReceiptPercentIcon,
  },
  {
    id: 'expiring-members',
    name: '만료 임박 회원',
    icon: UsersIcon,
  },
  // '결제 대시보드' 탭 제거 (2026-07-06) — '이번 달 매출' 카드 노출 방지.
  // 매출은 사이드바 '매출'(/owner/analytics)에서 비밀번호 잠금으로만 열람.
];

// 로딩 컴포넌트
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="text-gray-500">로딩 중...</div>
  </div>
);

// 에러 바운더리 컴포넌트
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 m-4">
          <h3 className="text-red-800 font-semibold mb-2">컴포넌트 로드 실패</h3>
          <p className="text-red-600 text-sm">{this.state.error?.message}</p>
          <button
            onClick={() => this.setState({ hasError: false })}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            다시 시도
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PaymentManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('vouchers-list');

  // 탭별 컴포넌트 렌더링
  const renderTabContent = () => {
    switch (activeTab) {
      case 'expiring-members':
        return <ExpiringMembersList />;
      case 'vouchers-list':
        return <VouchersList />;
      case 'add-voucher':
        return <AddVoucherForm />;
      case 'pricing-settings':
        return <PricingManagement />;
      case 'discount-reasons':
        return <DiscountReasonManagement />;
      default:
        return <VouchersList />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 페이지 헤더 */}
      <div className="bg-white border-b border-gray-200 mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                이용권 관리
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                이용권 발급, 설정 및 결제 내역 관리
              </p>
            </div>
          </div>

          {/* 이용권을 안 쓰는 매장이 스위치를 못 찾는 문제 — 스위치 자체는
              설정 > 운영 규칙 한 곳에만 두고 여기서는 위치만 알려준다 */}
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            이용권을 장부나 다른 프로그램으로 관리하신다면{' '}
            <Link
              to={ownerLayoutService.getOwnerPath('/owner/rules')}
              className="font-semibold underline underline-offset-2 hover:text-amber-700"
            >
              설정 &gt; 운영 규칙 &gt; 이용권 관리
            </Link>
            에서 꺼 두세요. 켜 둔 채로 이용권을 등록하지 않으면 회원이 자동으로
            &lsquo;만료&rsquo;가 되고, 만료된 회원은 출결 판정에서 빠져 미출석·지각·벌점이
            잡히지 않습니다.
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex -mb-px space-x-2 sm:space-x-4 md:space-x-8 overflow-x-auto scrollbar-hide" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm
                    transition-colors duration-150 ease-in-out whitespace-nowrap flex-shrink-0
                    ${isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon
                    className={`
                      mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5
                      ${isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `}
                    aria-hidden="true"
                  />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* 탭 컨텐츠 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            {renderTabContent()}
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default PaymentManagementPage;
